<template>
  <AppModal
    size="5xl"
    :title="`Batch upload ${definition.noun.plural}`"
    :disabled="processing"
    @close="$emit('close')"
  >
    <template #body>
      <div
        v-if="step === UploadSteps.Upload"
        class="p-2"
      >
        <p class="text-base">
          Download the template, fill in one {{ definition.noun.singular }} per row, and upload the completed spreadsheet to populate this section in one go.
        </p>

        <div class="light-warning-banner mb-6 mt-4">
          <strong>Please note:</strong> {{ definition.replaceWarning }}
        </div>

        <AppButtonGroup>
          <AppButton
            class="btn-outline btn-secondary"
            :disabled="processing"
            @click="handleDownloadTemplate"
          >
            <FontAwesomeIcon :icon="faDownload" />
            Download template
          </AppButton>

          <FileUploadButton
            class="btn-solid btn-secondary"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            :processing="processing"
            :icon="faFileArrowUp"
            text="Upload completed .xlsx"
            @file-selected="handleFileSelected"
          />
        </AppButtonGroup>
      </div>

      <div
        v-else-if="step === UploadSteps.Errors"
        class="p-2"
      >
        <h3 class="flex items-center gap-2 !mt-0 !mb-1 text-danger">
          <FontAwesomeIcon :icon="faCircleExclamation" />
          {{ errors.length }} {{ errors.length === 1 ? 'problem' : 'problems' }} found — nothing was imported
        </h3>

        <p class="text-base mb-4">
          Fix everything listed below, then upload the file again.
        </p>

        <ul class="flex flex-col gap-2 max-h-[420px] overflow-y-auto">
          <li
            v-for="(error, index) in errors"
            :key="index"
            class="border-l-4 border-danger bg-danger/5 px-4 py-2"
          >
            <span class="block text-xs font-semibold uppercase tracking-wide text-danger-dark">
              {{ error.row ? `Row ${error.row}` : 'Whole file' }}{{ error.column ? ` · ${error.column}` : '' }}
            </span>

            <span class="block text-sm text-black">
              {{ error.message }}
            </span>
          </li>
        </ul>
      </div>

      <div
        v-else
        class="p-2"
      >
        <p class="text-base font-medium">
          {{ definition.summarize(result) }}
        </p>

        <div class="light-warning-banner my-4">
          <strong>Warning:</strong> {{ definition.replaceWarning }} This cannot be undone once the plan is saved.
        </div>

        <div class="max-h-[420px] overflow-y-auto">
          <AppTable>
            <AppTableHeader>
              <th
                v-for="column in definition.previewColumns"
                :key="column.key"
              >
                {{ column.label }}
              </th>
            </AppTableHeader>

            <AppTableBody :colspan="definition.previewColumns.length">
              <tr
                v-for="(row, index) in definition.previewRows(result)"
                :key="index"
              >
                <td
                  v-for="column in definition.previewColumns"
                  :key="column.key"
                  class="align-top"
                >
                  {{ row[column.key] }}
                </td>
              </tr>
            </AppTableBody>
          </AppTable>
        </div>
      </div>
    </template>

    <template #footer>
      <AppButtonGroup class="w-full">
        <AppButton
          v-if="step === UploadSteps.Errors"
          class="btn-outline btn-secondary"
          @click="resetToUpload"
        >
          Choose another file
        </AppButton>

        <AppButton
          class="btn-outline btn-primary"
          :disabled="processing"
          @click="$emit('close')"
        >
          Cancel
        </AppButton>

        <AppButton
          v-if="step === UploadSteps.Preview"
          class="btn-solid btn-secondary"
          @click="handleConfirm"
        >
          Replace and import
        </AppButton>
      </AppButtonGroup>
    </template>
  </AppModal>
</template>

<script setup>
import { ref } from 'vue';

import { useToasts } from '@/composables';
import { extractTable, ImportFileError, mapAndValidate, downloadTemplate } from '@/composables/import';
import { usePlanStore } from '@/store';

import { AppButton, AppButtonGroup, AppModal } from '@/components/app';
import { FileUploadButton } from '@/components/buttons';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';

import { faCircleExclamation, faDownload, faFileArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const emit = defineEmits(['close']);

const props = defineProps({
  /** Import definition from @/composables/import (columns, validation hooks, commit). */
  definition: {
    type: Object,
    required: true,
  },
});

const planStore = usePlanStore();
const toasts = useToasts();

const UploadSteps = {
  Upload: 'upload',
  Errors: 'errors',
  Preview: 'preview',
};

const step = ref(UploadSteps.Upload);
const processing = ref(false);
const errors = ref([]);
const result = ref(null);

const resetToUpload = () => {
  errors.value = [];
  result.value = null;
  step.value = UploadSteps.Upload;
};

const handleDownloadTemplate = async () => {
  await downloadTemplate(props.definition);
};

const handleFileSelected = async (file) => {
  processing.value = true;

  try {
    const extracted = await extractTable(file);
    const outcome = mapAndValidate(props.definition, extracted, planStore);

    if (outcome.errors.length > 0) {
      errors.value = outcome.errors;
      step.value = UploadSteps.Errors;
    } else {
      result.value = outcome.result;
      step.value = UploadSteps.Preview;
    }
  } catch (error) {
    if (!(error instanceof ImportFileError)) console.error(error);

    errors.value = [{
      row: null,
      column: null,
      message: error instanceof ImportFileError ? error.message : 'Something went wrong while reading the file. Please try again.',
    }];
    step.value = UploadSteps.Errors;
  } finally {
    processing.value = false;
  }
};

const handleConfirm = () => {
  props.definition.commit(planStore, result.value);

  toasts.success(`Import complete. Remember to click Save to persist the new ${props.definition.noun.plural}.`);

  emit('close');
};
</script>
